/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content -- react-markdown supplies children through component props */
// markdown/classMap.js
import React from "react";
import Video from "../components/Video";

// Helper to merge base classnames with any passed-through props
function cx(base, extra) {
	return extra ? `${base} ${extra}` : base;
}

/** Global map of markdown → components with Tailwind classes */
export const classMap = {
	// Headings with ID generation for table of contents
	h1: ({ node, className, children, ...props }) => {
		const text = typeof children === "string" ? children : "";
		const id = text
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/(^-|-$)/g, "");
		return (
			<h1
				id={id}
				className={cx(
					"text-3xl lg:text-4xl font-bold mt-12 mb-4 text-center",
					className
				)}
				{...props}
			>
				{children}
			</h1>
		);
	},
	h2: ({ node, className, children, ...props }) => {
		const text = typeof children === "string" ? children : "";
		const id = text
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/(^-|-$)/g, "");
		return (
			<h2
				id={id}
				className={cx("text-2xl lg:text-3xl font-bold mb-2 mt-4", className)}
				{...props}
			>
				{children}
			</h2>
		);
	},
	h3: ({ node, className, children, ...props }) => {
		const text = typeof children === "string" ? children : "";
		const id = text
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/(^-|-$)/g, "");
		return (
			<h3
				id={id}
				className={cx("text-xl lg:text-2xl font-semibold mb-2 mt-4", className)}
				{...props}
			>
				{children}
			</h3>
		);
	},
	h4: ({ node, className, ...props }) => (
		<h4
			className={cx(
				"text-xl lg:text-2xl font-semibold mb-2 leading-tight",
				className
			)}
			{...props}
		/>
	),
	// treat as quote
	h5: ({ node, className, ...props }) => (
		<h5
			className={cx(
				"text-xl italic text-center mt-[-16px] lg:mt-[-32px] mb-6 lg:mb-8 border-l-4 pl-4 ml-8 text-gray-700",
				className
			)}
			{...props}
		/>
	),
	// treat as caption
	h6: ({ node, className, ...props }) => (
		<h6
			className={cx("text-sm italic text-center my-4 text-gray-700", className)}
			{...props}
		/>
	),
	// body
	p: ({ node, className, ...props }) => (
		<p
			className={cx(
				"font-serif text-neutral-800 text-base lg:text-lg",
				className
			)}
			{...props}
		/>
	),
	a: ({ node, className, ...props }) => (
		<a
			className={cx("text-[#FF5C00] hover:text-[#FF5C00] underline", className)}
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		/>
	),
	strong: ({ node, className, ...props }) => (
		<strong className={cx("font-semibold", className)} {...props} />
	),
	em: ({ node, className, ...props }) => (
		<em className={cx("italic", className)} {...props} />
	),

	// Lists
	ul: ({ node, className, ...props }) => (
		<ul
			className={cx("list-disc list-inside mb-4 ml-4 font-serif", className)}
			{...props}
		/>
	),
	ol: ({ node, className, ...props }) => (
		<ol
			className={cx("list-decimal list-inside mb-4 ml-4 font-serif", className)}
			{...props}
		/>
	),
	li: ({ node, className, ...props }) => (
		<li className={cx("", className)} {...props} />
	),

	// Quotes / Code
	blockquote: ({ node, className, ...props }) => (
		<blockquote
			className={cx("my-6 border-l-4 pl-4 text-gray-700 italic", className)}
			{...props}
		/>
	),
	pre: ({ node, className, ...props }) => (
		<pre
			className={cx(
				"my-4 rounded-2xl bg-gray-950 text-gray-100 p-4 overflow-x-auto",
				className
			)}
			{...props}
		/>
	),
	code: ({ inline, className, children, ...props }) =>
		inline ? (
			<code
				className={cx(
					"rounded bg-gray-100 px-1 py-0.5 text-[0.95em]",
					className
				)}
				{...props}
			>
				{children}
			</code>
		) : (
			<code className={cx("text-sm", className)} {...props}>
				{children}
			</code>
		),

	// Media
	img: ({ node, className, src, alt, ...props }) => {
		// Handle image mapping similar to current Blossom implementation
		return (
			<img
				src={src}
				alt={alt}
				className={cx(
					"w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto",
					className
				)}
				{...props}
			/>
		);
	},
	figure: ({ node, className, ...props }) => (
		<figure className={cx("my-6", className)} {...props} />
	),
	figcaption: ({ node, className, ...props }) => (
		<figcaption
			className={cx("mt-2 text-sm text-gray-500 text-center", className)}
			{...props}
		/>
	),

	hr: ({ node, className, ...props }) => (
		<hr className={cx("my-10 border-gray-200", className)} {...props} />
	),

	// Our synthetic node from remarkVideo → rehype: <video-block .../>
	"video-block": ({ node, ...props }) => {
		const { src, poster, caption, preload } = node?.properties || props || {};
		return (
			<Video src={src} poster={poster} caption={caption} preload={preload} />
		);
	},
};
